import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { SalesforceProduct } from '~/lib/salesforce/types';
import { getQueryValue } from '~/shared/utils/get-query-value';
import { VariantOption } from '~/features/product-details/types';

const getUrlProductId = (href: string) => {
    const query = href.split('?')[1];

    return query ? new URLSearchParams(query).get('id') ?? undefined : undefined;
};

/**
 * Which product the page is showing, and how to change it.
 *
 * Not every variant has its own url — some share a master product's — so the address bar
 * cannot always say which one is selected. Selection lives in state here, and resets
 * whenever the router lands on a different product (back button, or a variant with a url).
 */
export const useSelectedProductId = (initialData: SalesforceProduct | null | undefined) => {
    const router = useRouter();

    const routerProductId = getQueryValue(router.query.id) ?? initialData?.id;

    const [productId, setProductId] = useState(routerProductId);
    const [lastRouterProductId, setLastRouterProductId] = useState(routerProductId);

    if (lastRouterProductId !== routerProductId) {
        setLastRouterProductId(routerProductId);
        setProductId(routerProductId);
    }

    // `useRouter()` returns a new object per navigation; the ref keeps `selectVariant` stable.
    const routerRef = useRef(router);

    useEffect(() => {
        routerRef.current = router;
    }, [router]);

    const selectVariant = useCallback((option: VariantOption) => {
        if (!option.productId) return;

        setProductId(option.productId);

        // Following a url that points at a master product would select the master instead,
        // so variants without their own url switch in place.
        if (option.href && getUrlProductId(option.href) === option.productId) {
            // `scroll: false` is to prevent jumps when switching variants.
            routerRef.current.replace(option.href, undefined, { shallow: true, scroll: false });
        }
    }, []);

    return { productId, selectVariant };
};
